import { CodedProperty, ensure, NamedProperty } from "sprint-common-web";

import { ContentReferenceType } from "./ContentReferenceType";
import { ContentSubtype } from "./ContentSubtype";

export type ContentDetails = MovieContentDetails | TvSeriesContentDetails | AncillaryContentDetails;

export type TvSeriesContentDetails = BlankTvSeriesContentDetails | FullTvSeriesContentDetails;

export interface MovieContentDetails {
    type: ContentReferenceType.Movie;
    subtype: ContentSubtype;
    movie: NamedProperty;
    versionSummary: ContentDetailsVersionSummary;
    versions: ContentDetailsVersion[];
}

interface TvSeriesContentDetailsBase {
    type: ContentReferenceType.TvSeries;
    subtype: ContentSubtype;
    tvSeries: NamedProperty;

    versionSummary: ContentDetailsVersionSummary;
    versions: ContentDetailsVersion[];
}

interface BlankTvSeriesContentDetails extends TvSeriesContentDetailsBase {
    season?: NamedProperty;
    episode?: NamedProperty;
}

interface FullTvSeriesContentDetails extends TvSeriesContentDetailsBase {
    season: NamedProperty;
    episode: NamedProperty;
}

export interface AncillaryContentDetails {
    type: ContentReferenceType.Ancillary;
    subtype: ContentSubtype;
    ancillary: NamedProperty;
    airline: CodedProperty;
    versionSummary: ContentDetailsVersionSummary;
}

interface BaseContentDetailsVersion {
    id: string;
    buzzId?: string;
    type: NamedProperty;
}

interface ContentDetailsVersion extends BaseContentDetailsVersion {
    brandings?: CodedProperty[];
    distributor: NamedProperty;
}

interface ContentDetailsVersionSummary {
    ids: string[];
    buzzIds?: string[];
    versionType: NamedProperty;
    distributors: NamedProperty[];
    brandings: CodedProperty[];
}

export const isMovieContentDetails = (details: ContentDetails): details is MovieContentDetails =>
    details.type === ContentReferenceType.Movie;

export const isTvSeriesContentDetails = (details: ContentDetails): details is TvSeriesContentDetails =>
    details.type === ContentReferenceType.TvSeries;

export const isAncillaryContentDetails = (details: ContentDetails): details is AncillaryContentDetails =>
    details.type === ContentReferenceType.Ancillary;

export const isBlankTvSeriesContentDetails = (details: ContentDetails): details is BlankTvSeriesContentDetails =>
    isTvSeriesContentDetails(details) && (!details.season || !details.episode);

export const isFullTvSeriesContentDetails = (details: ContentDetails): details is FullTvSeriesContentDetails =>
    isTvSeriesContentDetails(details) && !!details.season && !!details.episode;

export const ensureMovieContentDetails: (value: ContentDetails) => asserts value is MovieContentDetails = (value) => {
    ensure(isMovieContentDetails(value), "Expected Movie Content Details");
};

export const ensureTvSeriesContentDetails: (value: ContentDetails) => asserts value is TvSeriesContentDetails = (
    value
) => {
    ensure(isTvSeriesContentDetails(value), "Expected TV Series Content Details");
};

export const ensureFullTvSeriesContentDetails: (
    value: ContentDetails
) => asserts value is FullTvSeriesContentDetails = (value) => {
    ensure(isFullTvSeriesContentDetails(value), "Expected Full TV Series Content Details");
};

export const ensureAncillaryContentDetails: (value: ContentDetails) => asserts value is AncillaryContentDetails = (
    value
) => {
    ensure(isAncillaryContentDetails(value), "Expected Ancillary Content Details");
};
